import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Sparkles, Trash2, Upload } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full border border-line bg-forest px-3 py-2.5 text-sm text-cream placeholder:text-sage/50 outline-none transition-colors duration-300 focus:border-harvest";

async function uploadPhoto(file) {
  const token = localStorage.getItem("jamna_admin_token");
  const fd = new FormData();
  fd.append("file", file);
  const { data } = await axios.post(`${API}/upload`, fd, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return `${process.env.REACT_APP_BACKEND_URL}/api/files/${data.path}`;
}

export function PhotoField({ label, value, onChange }) {
  const [uploading, setUploading] = useState(false);
  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const url = await uploadPhoto(f);
      onChange(url);
      toast.success("Photo upload ho gayi!");
    } catch {
      toast.error("Upload nahi hui. Dobara try karein.");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };
  return (
    <div>
      <Field label={label} value={value} onChange={onChange} />
      <label
        data-testid="photo-upload-button"
        className="mt-2 inline-flex cursor-pointer items-center gap-2 border border-harvest px-4 py-2 text-xs uppercase tracking-[0.15em] text-harvest transition-colors duration-300 hover:bg-harvest hover:text-forest"
      >
        <Upload className="h-3.5 w-3.5" /> {uploading ? "Uploading…" : "Upload Photo"}
        <input
          type="file"
          accept="image/*"
          className="hidden"
          data-testid="photo-upload-input"
          onChange={onFile}
        />
      </label>
      <div className="mt-2">
        <PhotoPreview url={value} />
      </div>
    </div>
  );
}

export function EnquiriesList() {
  const [items, setItems] = useState(null);
  const token = localStorage.getItem("jamna_admin_token");
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  useEffect(() => {
    axios
      .get(`${API}/enquiries`, auth)
      .then(({ data }) =>
        setItems([...data].sort((a, b) => new Date(b.created_at) - new Date(a.created_at)))
      )
      .catch(() => setItems([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/enquiries/${id}`, auth);
      setItems((list) => list.filter((e) => e.id !== id));
      toast.success("Enquiry delete ho gayi.");
    } catch {
      toast.error("Delete nahi hui. Dobara try karein.");
    }
  };

  if (!items)
    return <p className="text-sm uppercase tracking-[0.2em] text-sage">Loading…</p>;
  if (items.length === 0)
    return (
      <p
        data-testid="enquiries-empty"
        className="border border-line bg-moss/60 p-8 text-center text-sm text-sage"
      >
        Abhi koi enquiry nahi aayi. Nayi enquiry aate hi yahan dikhegi.
      </p>
    );
  return (
    <div className="space-y-4" data-testid="enquiries-list">
      <p className="text-sm text-sage">{items.length} enquiries — sabse nayi upar</p>
      {items.map((en) => (
        <div key={en.id} data-testid={`enquiry-${en.id}`} className="border border-line bg-moss/60 p-5">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="font-display text-lg text-cream">{en.name}</p>
            <div className="flex items-center gap-2">
              <span className="border border-line px-3 py-1 text-[11px] uppercase tracking-[0.15em] text-harvest">
                {en.product || "General"}
              </span>
              <button
                type="button"
                onClick={() => remove(en.id)}
                data-testid={`enquiry-delete-${en.id}`}
                aria-label={`Delete enquiry from ${en.name}`}
                className="border border-line p-2 text-sage transition-colors duration-300 hover:border-red-400 hover:text-red-400"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          </div>
          <a
            href={`tel:${(en.phone || "").replace(/\s/g, "")}`}
            className="mt-1 block text-sm text-harvest transition-colors duration-300 hover:text-harvestHover"
          >
            {en.phone}
          </a>
          <p className="mt-3 text-sm leading-relaxed text-sage">{en.requirement}</p>
          <p className="mt-3 text-[11px] uppercase tracking-[0.15em] text-sage/60">
            {new Date(en.created_at).toLocaleString("en-IN", {
              day: "2-digit",
              month: "short",
              year: "numeric",
              hour: "2-digit",
              minute: "2-digit",
            })}
          </p>
        </div>
      ))}
    </div>
  );
}

export function Field({ label, value, onChange, textarea, rows = 3 }) {
  return (
    <div>
      <label className="mb-1.5 block text-xs uppercase tracking-[0.15em] text-sage">
        {label}
      </label>
      {textarea ? (
        <textarea
          rows={rows}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className={`${inputCls} resize-y`}
        />
      ) : (
        <input value={value} onChange={(e) => onChange(e.target.value)} className={inputCls} />
      )}
    </div>
  );
}

function PhotoPreview({ url }) {
  if (!url) return null;
  return (
    <img
      src={url}
      alt="preview"
      data-testid="photo-preview"
      className="h-32 border border-line object-cover"
    />
  );
}

function ListShell({ title, onAdd, children }) {
  return (
    <div>
      <div className="mb-5 flex items-center justify-between">
        <p className="text-sm text-sage">{title}</p>
        <button
          type="button"
          onClick={onAdd}
          data-testid="add-item-button"
          className="inline-flex items-center gap-2 border border-harvest px-4 py-2 text-xs uppercase tracking-[0.15em] text-harvest transition-colors duration-300 hover:bg-harvest hover:text-forest"
        >
          <Plus className="h-3.5 w-3.5" /> Add New
        </button>
      </div>
      <div className="space-y-6">{children}</div>
    </div>
  );
}

function ItemCard({ title, onDelete, children }) {
  return (
    <div className="border border-line bg-moss/60 p-5">
      <div className="mb-4 flex items-center justify-between gap-4">
        <p className="font-display text-lg text-cream">{title}</p>
        <button
          type="button"
          onClick={onDelete}
          aria-label={`Delete ${title}`}
          className="border border-line p-2 text-sage transition-colors duration-300 hover:border-red-400 hover:text-red-400"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </div>
      <div className="space-y-4">{children}</div>
    </div>
  );
}

export function HeroEditor({ value, onChange }) {
  const set = (k) => (v) => onChange({ ...value, [k]: v });
  return (
    <div className="space-y-5" data-testid="hero-editor">
      <Field label="Overline (upar chhota text)" value={value.overline} onChange={set("overline")} />
      <Field label="Headline — Line 1" value={value.line1} onChange={set("line1")} />
      <Field label="Headline — Line 2 start" value={value.line2a} onChange={set("line2a")} />
      <Field label="Headline — Highlight word" value={value.line2b} onChange={set("line2b")} />
      <Field label="Subheadline" textarea value={value.sub} onChange={set("sub")} />
      <Field label="Tagline (bold text)" value={value.tagline} onChange={set("tagline")} />
      <PhotoField label="Hero Photo" value={value.image} onChange={set("image")} />
    </div>
  );
}

export function ContactEditor({ value, onChange }) {
  const set = (k) => (v) => onChange({ ...value, [k]: v });
  const social = value.social || {};
  const setSocial = (k) => (v) => onChange({ ...value, social: { ...social, [k]: v } });
  return (
    <div className="space-y-5" data-testid="contact-editor">
      <Field label="Phone" value={value.phone} onChange={set("phone")} />
      <Field label="Email" value={value.email} onChange={set("email")} />
      <Field label="Address" value={value.address} onChange={set("address")} />
      <Field label="GSTIN" value={value.gstin || ""} onChange={set("gstin")} />
      <p className="border-t border-line pt-4 text-xs uppercase tracking-[0.2em] text-harvest">
        Social Media Links (footer icons — khali chhodo to icon nahi dikhega)
      </p>
      <Field label="Facebook Page URL" value={social.facebook || ""} onChange={setSocial("facebook")} />
      <Field label="Instagram Page URL" value={social.instagram || ""} onChange={setSocial("instagram")} />
      <Field label="Twitter / X URL" value={social.twitter || ""} onChange={setSocial("twitter")} />
      <Field label="LinkedIn Page URL" value={social.linkedin || ""} onChange={setSocial("linkedin")} />
    </div>
  );
}

function AiDescribeButton({ productId, modelName, onResult, testid }) {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      const d = await aiDescribe(productId, modelName);
      onResult(d);
      toast.success("AI ne content likh diya! Check karke Save karein.");
    } catch {
      toast.error("AI generate nahi ho paya. Dobara try karein.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <button
      type="button"
      onClick={run}
      disabled={busy}
      data-testid={testid || (modelName ? "ai-describe-model" : "ai-describe-button")}
      className="inline-flex items-center gap-2 border border-harvest px-4 py-2 text-xs uppercase tracking-[0.15em] text-harvest transition-colors duration-300 hover:bg-harvest hover:text-forest disabled:opacity-50"
    >
      <Sparkles className="h-3.5 w-3.5" />
      {busy ? "AI likh raha hai…" : modelName ? "AI Specs" : "AI Se Description + Specs Likho"}
    </button>
  );
}

function ModelsEditor({ productId, models, onChange }) {
  const [open, setOpen] = useState(false);
  const [uploadingIdx, setUploadingIdx] = useState(-1);
  const [bulk, setBulk] = useState(null);
  const setM = (i, item) => onChange(models.map((m, idx) => (idx === i ? item : m)));
  const add = () => onChange([...models, { name: "New Model", img: "" }]);
  const del = (i) => {
    if (!window.confirm(`Model "${models[i].name}" delete karein?`)) return;
    onChange(models.filter((_, idx) => idx !== i));
  };
  const onFile = (i) => async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploadingIdx(i);
    try {
      const url = await uploadPhoto(f);
      setM(i, { ...models[i], img: url });
      toast.success("Model photo upload ho gayi!");
    } catch {
      toast.error("Upload nahi hui. Dobara try karein.");
    } finally {
      setUploadingIdx(-1);
      e.target.value = "";
    }
  };

  const norm = (s) => (s || "").toLowerCase().replace(/[^a-z0-9]+/g, "");
  const cleanName = (fn) =>
    fn.replace(/\.[a-z0-9]+$/i, "").replace(/[_-]+/g, " ").replace(/\s+/g, " ").trim();

  const onBulk = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    let list = [...models];
    let matched = 0;
    let created = 0;
    let failed = 0;
    for (let i = 0; i < files.length; i++) {
      setBulk({ done: i, total: files.length });
      try {
        const url = await uploadPhoto(files[i]);
        const key = norm(cleanName(files[i].name));
        const idx = list.findIndex((m) => norm(m.name) === key);
        if (idx >= 0) {
          list[idx] = { ...list[idx], img: url };
          matched++;
        } else {
          list.push({ name: cleanName(files[i].name), img: url });
          created++;
        }
      } catch {
        failed++;
      }
    }
    setBulk(null);
    onChange(list);
    if (failed) toast.error(`${failed} photos upload nahi hui.`);
    toast.success(
      `Bulk upload poora: ${matched} models ki photo lagi${created ? `, ${created} naye models bane` : ""}. Save karna na bhoolein!`
    );
  };
  return (
    <div className="border border-line p-4" data-testid="models-editor">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid="models-toggle"
        className="flex w-full items-center justify-between text-xs uppercase tracking-[0.15em] text-harvest"
      >
        <span>Complete Range Models ({models.length}) — photo page par gallery mein dikhte hain</span>
        <span>{open ? "Band Karein ▲" : "Edit Karein ▼"}</span>
      </button>
      {open && (
        <div className="mt-4 space-y-2.5">
          {models.map((m, i) => (
            <div key={i} className="flex items-center gap-2" data-testid={`model-row-${i}`}>
              {m.img ? (
                <img src={m.img} alt="" className="h-10 w-10 shrink-0 border border-line bg-white object-contain" />
              ) : (
                <span className="h-10 w-10 shrink-0 border border-line bg-moss" />
              )}
              <input
                value={m.name}
                onChange={(e) => setM(i, { ...m, name: e.target.value })}
                className={inputCls}
                data-testid={`model-name-${i}`}
                aria-label={`Model name ${i + 1}`}
              />
              <input
                value={(m.specs || []).join(", ")}
                onChange={(e) => setM(i, { ...m, specs: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })}
                placeholder="Specs (comma se alag)"
                className={inputCls}
                data-testid={`model-specs-${i}`}
                aria-label={`Model specs ${i + 1}`}
              />
              <label
                data-testid={`model-upload-${i}`}
                className="shrink-0 cursor-pointer border border-line p-2 text-sage transition-colors duration-300 hover:border-harvest hover:text-harvest"
              >
                <Upload className="h-3.5 w-3.5" />
                <input type="file" accept="image/*" className="hidden" onChange={onFile(i)} disabled={uploadingIdx === i} />
              </label>
              <AiDescribeButton
                productId={productId}
                modelName={m.name}
                testid={`ai-describe-model-${i}`}
                onResult={(d) => setM(i, { ...models[i], specs: d.specs?.length ? d.specs : models[i].specs })}
              />
              <button
                type="button"
                onClick={() => del(i)}
                data-testid={`model-delete-${i}`}
                aria-label={`Delete model ${m.name}`}
                className="shrink-0 border border-line p-2 text-sage transition-colors duration-300 hover:border-red-400 hover:text-red-400"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
          <div className="flex flex-wrap items-center gap-3 pt-1">
            <button
              type="button"
              onClick={add}
              data-testid="add-model-button"
              className="inline-flex items-center gap-2 border border-harvest px-4 py-2 text-xs uppercase tracking-[0.15em] text-harvest transition-colors duration-300 hover:bg-harvest hover:text-forest"
            >
              <Plus className="h-3.5 w-3.5" /> Add Model
            </button>
            <label
              data-testid="models-bulk-upload"
              className={`inline-flex items-center gap-2 border px-4 py-2 text-xs uppercase tracking-[0.15em] transition-colors duration-300 ${
                bulk
                  ? "cursor-wait border-line text-sage"
                  : "cursor-pointer border-harvest text-harvest hover:bg-harvest hover:text-forest"
              }`}
            >
              <Upload className="h-3.5 w-3.5" />
              {bulk ? `Uploading ${bulk.done}/${bulk.total}…` : "Bulk Upload Photos"}
              <input
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                data-testid="models-bulk-input"
                onChange={onBulk}
                disabled={!!bulk}
              />
            </label>
            <span className="text-[11px] text-sage/70">
              File ka naam model ke naam jaisa rakhein (e.g. "Jamna 5022 SD U.jpg") — photo apne aap sahi model par lag jayegi. Jo match na ho wo naya model ban jayega.
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

const aiDescribe = async (productId, modelName) => {
  const token = localStorage.getItem("jamna_admin_token");
  const { data } = await axios.post(
    `${API}/ai/describe`,
    { product_id: productId, model_name: modelName || null },
    { headers: { Authorization: `Bearer ${token}` }, timeout: 60000 }
  );
  return data;
};

export function ProductsEditor({ value, onChange }) {
  const setItem = (i, item) => onChange(value.map((p, idx) => (idx === i ? item : p)));
  const add = () =>
    onChange([
      ...value,
      {
        id: `product-${Date.now()}`,
        num: String(value.length + 1).padStart(2, "0"),
        name: "New Product",
        hindi: "",
        desc: "",
        features: [],
        range: [],
        specs: [],
        img: "",
        alt: "",
      },
    ]);
  const del = (i) => onChange(value.filter((_, idx) => idx !== i));

  return (
    <ListShell title={`${value.length} products — har ek ka apna detail page banta hai`} onAdd={add}>
      {value.map((p, i) => (
        <ItemCard key={p.id} title={p.name} onDelete={() => del(i)}>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Name" value={p.name} onChange={(v) => setItem(i, { ...p, name: v })} />
            <Field label="Hindi Name" value={p.hindi} onChange={(v) => setItem(i, { ...p, hindi: v })} />
          </div>
          <Field label="Description" textarea value={p.desc} onChange={(v) => setItem(i, { ...p, desc: v })} />
          <AiDescribeButton productId={p.id} onResult={(d) => setItem(i, { ...p, desc: d.description || p.desc, features: d.features?.length ? d.features : p.features, specs: d.specs?.length ? d.specs : p.specs })} />
          <PhotoField label="Photo" value={p.img} onChange={(v) => setItem(i, { ...p, img: v })} />
          <Field label="Photo Alt Text" value={p.alt} onChange={(v) => setItem(i, { ...p, alt: v })} />
          <Field
            label="Features (har line mein ek)"
            textarea
            rows={4}
            value={(p.features || []).join("\n")}
            onChange={(v) => setItem(i, { ...p, features: v.split("\n") })}
          />
          <Field
            label="Range Includes (comma se alag)"
            value={(p.range || []).join(", ")}
            onChange={(v) => setItem(i, { ...p, range: v.split(",").map((s) => s.trim()) })}
          />
          <Field
            label="Items / Fittings List (har line mein ek item)"
            textarea
            rows={6}
            value={(p.items || []).join("\n")}
            onChange={(v) => setItem(i, { ...p, items: v.split("\n") })}
          />
          <Field
            label="Specs (comma se alag)"
            value={(p.specs || []).join(", ")}
            onChange={(v) => setItem(i, { ...p, specs: v.split(",").map((s) => s.trim()) })}
          />
          <ModelsEditor productId={p.id} models={p.models || []} onChange={(v) => setItem(i, { ...p, models: v })} />
        </ItemCard>
      ))}
    </ListShell>
  );
}

export function StoriesEditor({ value, onChange }) {
  const setItem = (i, item) => onChange(value.map((s, idx) => (idx === i ? item : s)));
  const add = () => onChange([...value, { name: "Naya Kisan", place: "", crop: "", quote: "" }]);
  const del = (i) => onChange(value.filter((_, idx) => idx !== i));

  return (
    <ListShell title={`${value.length} success stories`} onAdd={add}>
      {value.map((s, i) => (
        <ItemCard key={i} title={s.name} onDelete={() => del(i)}>
          <div className="grid gap-4 sm:grid-cols-3">
            <Field label="Name" value={s.name} onChange={(v) => setItem(i, { ...s, name: v })} />
            <Field label="Place" value={s.place} onChange={(v) => setItem(i, { ...s, place: v })} />
            <Field label="Crop — Product" value={s.crop} onChange={(v) => setItem(i, { ...s, crop: v })} />
          </div>
          <Field label="Quote" textarea value={s.quote} onChange={(v) => setItem(i, { ...s, quote: v })} />
        </ItemCard>
      ))}
    </ListShell>
  );
}

export function UpdatesEditor({ value, onChange }) {
  const setItem = (i, item) => onChange(value.map((u, idx) => (idx === i ? item : u)));
  const add = () => onChange([{ date: "", tag: "News", title: "" }, ...value]);
  const del = (i) => onChange(value.filter((_, idx) => idx !== i));

  return (
    <ListShell title={`${value.length} updates`} onAdd={add}>
      {value.map((u, i) => (
        <ItemCard key={i} title={u.title || "New update"} onDelete={() => del(i)}>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Date" value={u.date} onChange={(v) => setItem(i, { ...u, date: v })} />
            <Field label="Tag" value={u.tag} onChange={(v) => setItem(i, { ...u, tag: v })} />
          </div>
          <Field label="Title" textarea rows={2} value={u.title} onChange={(v) => setItem(i, { ...u, title: v })} />
        </ItemCard>
      ))}
    </ListShell>
  );
}

const iconOptions = ["wheat", "sprout", "leaf", "carrot", "apple", "flower"];

export function CropsEditor({ value, onChange }) {
  const setItem = (i, item) => onChange(value.map((c, idx) => (idx === i ? item : c)));
  const add = () => onChange([...value, { icon: "sprout", name: "", system: "", saving: "" }]);
  const del = (i) => onChange(value.filter((_, idx) => idx !== i));

  return (
    <ListShell title={`${value.length} crops`} onAdd={add}>
      {value.map((c, i) => (
        <ItemCard key={i} title={c.name || "New crop"} onDelete={() => del(i)}>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Crop Name" value={c.name} onChange={(v) => setItem(i, { ...c, name: v })} />
            <Field label="Recommended System" value={c.system} onChange={(v) => setItem(i, { ...c, system: v })} />
            <Field label="Water Saving (e.g. 50%)" value={c.saving} onChange={(v) => setItem(i, { ...c, saving: v })} />
            <div>
              <label className="mb-1.5 block text-xs uppercase tracking-[0.15em] text-sage">Icon</label>
              <select
                value={c.icon}
                onChange={(e) => setItem(i, { ...c, icon: e.target.value })}
                className={`${inputCls} cursor-pointer`}
              >
                {iconOptions.map((o) => (
                  <option key={o} value={o} className="bg-forest text-cream">
                    {o}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </ItemCard>
      ))}
    </ListShell>
  );
}

export function TeamEditor({ value, onChange }) {
  const setItem = (i, item) => onChange(value.map((e, idx) => (idx === i ? item : e)));
  const add = () =>
    onChange([...value, { name: "Naya Employee", designation: "", sales: "", photo: "" }]);
  const del = (i) => onChange(value.filter((_, idx) => idx !== i));

  return (
    <ListShell title={`${value.length} team members — jitne chahe add/remove karein`} onAdd={add}>
      {value.map((e, i) => (
        <ItemCard key={i} title={e.name} onDelete={() => del(i)}>
          <div className="grid gap-4 sm:grid-cols-3">
            <Field label="Name" value={e.name} onChange={(v) => setItem(i, { ...e, name: v })} />
            <Field
              label="Designation"
              value={e.designation}
              onChange={(v) => setItem(i, { ...e, designation: v })}
            />
            <Field
              label="Total Sales (e.g. ₹12.5 Lakh)"
              value={e.sales}
              onChange={(v) => setItem(i, { ...e, sales: v })}
            />
          </div>
          <PhotoField
            label="Photo (optional)"
            value={e.photo || ""}
            onChange={(v) => setItem(i, { ...e, photo: v })}
          />
        </ItemCard>
      ))}
    </ListShell>
  );
}

export function AppEditor({ value, onChange }) {
  const set = (k) => (v) => onChange({ ...value, [k]: v });
  const [uploading, setUploading] = useState(false);

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.name.toLowerCase().endsWith(".apk")) {
      toast.error("Sirf .apk file upload karein");
      return;
    }
    setUploading(true);
    try {
      const token = localStorage.getItem("jamna_admin_token");
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await axios.post(`${API}/upload`, fd, {
        headers: { Authorization: `Bearer ${token}` },
      });
      set("apkUrl")(`${process.env.REACT_APP_BACKEND_URL}/api/files/${data.path}?download=1`);
      toast.success("APK upload ho gayi! Save karna na bhoolein.");
    } catch {
      toast.error("Upload nahi hui. Dobara try karein.");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  return (
    <div className="space-y-5" data-testid="app-editor">
      <Field label="App Name" value={value.name} onChange={set("name")} />
      <Field label="Version" value={value.version} onChange={set("version")} />
      <Field label="Description" textarea value={value.desc} onChange={set("desc")} />
      <div>
        <label className="mb-1.5 block text-xs uppercase tracking-[0.15em] text-sage">
          APK File (Android App)
        </label>
        <label
          data-testid="apk-upload-button"
          className="inline-flex cursor-pointer items-center gap-2 border border-harvest px-4 py-2 text-xs uppercase tracking-[0.15em] text-harvest transition-colors duration-300 hover:bg-harvest hover:text-forest"
        >
          <Upload className="h-3.5 w-3.5" /> {uploading ? "Uploading…" : "Upload APK"}
          <input
            type="file"
            accept=".apk"
            className="hidden"
            data-testid="apk-upload-input"
            onChange={onFile}
          />
        </label>
        {value.apkUrl ? (
          <p className="mt-2 break-all text-xs text-harvest" data-testid="apk-current">
            Current APK: {value.apkUrl}
          </p>
        ) : (
          <p className="mt-2 text-xs text-sage/60">Abhi koi APK upload nahi hai — site pe "Coming Soon" dikhta hai.</p>
        )}
      </div>
    </div>
  );
}

export function FittingsEditor({ value, onChange }) {
  const setItem = (i, item) => onChange(value.map((f, idx) => (idx === i ? item : f)));
  const add = () =>
    onChange([
      ...value,
      { id: `fitting-${Date.now()}`, name: "New Fitting", hindi: "", desc: "", img: "", alt: "" },
    ]);
  const del = (i) => onChange(value.filter((_, idx) => idx !== i));

  return (
    <ListShell title={`${value.length} fittings — photos aur description ke saath`} onAdd={add}>
      {value.map((f, i) => (
        <ItemCard key={f.id} title={f.name} onDelete={() => del(i)}>
          <div className="grid gap-4 sm:grid-cols-3">
            <Field label="Name" value={f.name} onChange={(v) => setItem(i, { ...f, name: v })} />
            <Field label="Hindi Name" value={f.hindi} onChange={(v) => setItem(i, { ...f, hindi: v })} />
            <Field
              label="Price (optional, e.g. ₹45/pc)"
              value={f.price || ""}
              onChange={(v) => setItem(i, { ...f, price: v })}
            />
          </div>
          <Field label="Description" textarea value={f.desc} onChange={(v) => setItem(i, { ...f, desc: v })} />
          <PhotoField label="Photo" value={f.img} onChange={(v) => setItem(i, { ...f, img: v })} />
        </ItemCard>
      ))}
    </ListShell>
  );
}


export function ManifestoEditor({ value, onChange }) {
  const set = (k) => (v) => onChange({ ...value, [k]: v });
  const chapters = value.chapters || [];
  const setCh = (i, ch) =>
    onChange({ ...value, chapters: chapters.map((c, idx) => (idx === i ? ch : c)) });
  return (
    <div className="space-y-5" data-testid="manifesto-editor">
      <Field label="Overline (chhota heading)" value={value.overline || ""} onChange={set("overline")} />
      <Field label="Heading Part A" value={value.headingA || ""} onChange={set("headingA")} />
      <Field label="Heading Accent (highlight word)" value={value.headingAccent || ""} onChange={set("headingAccent")} />
      <Field label="Heading Part B" value={value.headingB || ""} onChange={set("headingB")} />
      <PhotoField label="Section Photo" value={value.image || ""} onChange={set("image")} />
      <Field label="Photo Caption" value={value.caption || ""} onChange={set("caption")} />
      <p className="border-t border-line pt-4 text-xs uppercase tracking-[0.2em] text-harvest">
        Chapters (Why Jamna points)
      </p>
      {chapters.map((c, i) => (
        <div key={i} data-testid={`chapter-row-${i}`} className="space-y-3 border border-line p-4">
          <div className="flex items-end justify-between gap-3">
            <div className="w-28">
              <Field label="Number" value={c.num || ""} onChange={(v) => setCh(i, { ...c, num: v })} />
            </div>
            <button
              type="button"
              onClick={() => onChange({ ...value, chapters: chapters.filter((_, idx) => idx !== i) })}
              aria-label={`Delete chapter ${c.num}`}
              className="border border-line p-2 text-sage transition-colors duration-300 hover:border-red-400 hover:text-red-400"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
          <Field label="Title" value={c.title || ""} onChange={(v) => setCh(i, { ...c, title: v })} />
          <Field label="Text" textarea value={c.text || ""} onChange={(v) => setCh(i, { ...c, text: v })} />
        </div>
      ))}
      <button
        type="button"
        onClick={() =>
          onChange({ ...value, chapters: [...chapters, { num: String(chapters.length + 1).padStart(2, "0"), title: "Naya Point", text: "" }] })
        }
        data-testid="add-chapter-button"
        className="inline-flex items-center gap-2 border border-harvest px-4 py-2 text-xs uppercase tracking-[0.15em] text-harvest transition-colors duration-300 hover:bg-harvest hover:text-forest"
      >
        <Plus className="h-3.5 w-3.5" /> Add Chapter
      </button>
    </div>
  );
}

export function CertificationsEditor({ value, onChange }) {
  const list = value || [];
  const setC = (i, item) => onChange(list.map((c, idx) => (idx === i ? item : c)));
  return (
    <div className="space-y-3" data-testid="certifications-editor">
      {list.map((c, i) => (
        <div key={i} data-testid={`cert-row-${i}`} className="flex items-center gap-2">
          <input
            value={c.code || ""}
            onChange={(e) => setC(i, { ...c, code: e.target.value })}
            placeholder="IS 4984"
            className={inputCls}
            data-testid={`cert-code-${i}`}
            aria-label={`Certification code ${i + 1}`}
          />
          <input
            value={c.label || ""}
            onChange={(e) => setC(i, { ...c, label: e.target.value })}
            placeholder="HDPE Pipes Certified"
            className={inputCls}
            data-testid={`cert-label-${i}`}
            aria-label={`Certification label ${i + 1}`}
          />
          <button
            type="button"
            onClick={() => onChange(list.filter((_, idx) => idx !== i))}
            aria-label={`Delete certification ${c.code}`}
            className="shrink-0 border border-line p-2 text-sage transition-colors duration-300 hover:border-red-400 hover:text-red-400"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      ))}
      <button
        type="button"
        onClick={() => onChange([...list, { code: "", label: "" }])}
        data-testid="add-cert-button"
        className="inline-flex items-center gap-2 border border-harvest px-4 py-2 text-xs uppercase tracking-[0.15em] text-harvest transition-colors duration-300 hover:bg-harvest hover:text-forest"
      >
        <Plus className="h-3.5 w-3.5" /> Add Certification
      </button>
    </div>
  );
}


export function StatsEditor({ value, onChange }) {
  const list = value || [];
  const setS = (i, item) => onChange(list.map((s, idx) => (idx === i ? item : s)));
  return (
    <div className="space-y-3" data-testid="stats-editor">
      {list.map((s, i) => (
        <div key={i} data-testid={`stat-row-${i}`} className="flex items-center gap-2">
          <input
            value={s.value || ""}
            onChange={(e) => setS(i, { ...s, value: e.target.value })}
            placeholder="25+"
            className={inputCls}
            data-testid={`stat-value-${i}`}
            aria-label={`Stat value ${i + 1}`}
          />
          <input
            value={s.label || ""}
            onChange={(e) => setS(i, { ...s, label: e.target.value })}
            placeholder="Years of Manufacturing Trust"
            className={inputCls}
            data-testid={`stat-label-${i}`}
            aria-label={`Stat label ${i + 1}`}
          />
          <button
            type="button"
            onClick={() => onChange(list.filter((_, idx) => idx !== i))}
            aria-label={`Delete stat ${s.value}`}
            className="shrink-0 border border-line p-2 text-sage transition-colors duration-300 hover:border-red-400 hover:text-red-400"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      ))}
      <button
        type="button"
        onClick={() => onChange([...list, { value: "", label: "" }])}
        data-testid="add-stat-button"
        className="inline-flex items-center gap-2 border border-harvest px-4 py-2 text-xs uppercase tracking-[0.15em] text-harvest transition-colors duration-300 hover:bg-harvest hover:text-forest"
      >
        <Plus className="h-3.5 w-3.5" /> Add Stat
      </button>
    </div>
  );
}
